import { Group, Stack, TextInput } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { InsightType } from '../../../../api/insights-client/insights-client.type';
import { useTranslation } from 'react-i18next';
import { EmptyFilterResult } from '../../../../components/filters/empty-filter-result';
import { FloraInsightsView } from '../../../../components/insights/flora-insights-view';
import { baseWhite, inkDark, inkLight, purpleBase } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Table } from '../../../data-management/components/table';
import type { RiskAnalysisTask } from '../risk-analysis.client.type';
import { areDataArraysDifferent, sortRiskAnalysisData } from '../risk-analysis.helpers';
import { getTaskTableColumns, handleTaskTableDownloadCSV } from './risk-analysis-task-table.hooks';

const ITEMS_PER_PAGE = 20;

export function RiskAnalysisTaskTable({
  tasks,
  onTaskClick,
  isEpic = false,
  activeTab = 'table',
  onActiveTabChange,
  floraInsightTypeFilters = [],
  floraSeverityFilters = [],
  floraStatusFilters = [],
  floraViewFilter = 'active',
  onInsightClick,
  showInsightsTab = true,
}: {
  tasks: RiskAnalysisTask[];
  onTaskClick: (task: RiskAnalysisTask) => void;
  isEpic?: boolean;
  activeTab?: 'table' | 'flora';
  onActiveTabChange?: (tab: 'table' | 'flora') => void;
  floraInsightTypeFilters?: string[];
  floraSeverityFilters?: string[];
  floraStatusFilters?: string[];
  floraViewFilter?: string;
  onInsightClick?: (insightId: string) => void;
  showInsightsTab?: boolean;
}) {
  const i18n = useTranslation();
  const [selectedPage, setSelectedPage] = useState(1);
  const [filteredTasks, setFilteredTasks] = useState(tasks);
  const [searchTerm, setSearchTerm] = useState('');
  const prevTasksRef = useRef<RiskAnalysisTask[]>(tasks);
  const prevIsEpicRef = useRef<boolean>(isEpic);

  // Reset search and filtered tasks only when tasks content or isEpic actually changes
  useEffect(() => {
    const tasksChanged = areDataArraysDifferent(prevTasksRef.current, tasks);
    const isEpicChanged = prevIsEpicRef.current !== isEpic;

    if (tasksChanged || isEpicChanged) {
      setSearchTerm('');
      setFilteredTasks(tasks);
      setSelectedPage(1);

      // Update refs to current values
      prevTasksRef.current = tasks;
      prevIsEpicRef.current = isEpic;
    }
  }, [tasks, isEpic]);

  const sortData = (
    data: RiskAnalysisTask[],
    sortBy: keyof RiskAnalysisTask,
    direction: 'asc' | 'desc',
  ): RiskAnalysisTask[] => {
    return sortRiskAnalysisData(data, sortBy, direction);
  };

  return (
    <Stack style={{ position: 'relative' }}>
      <Group justify="space-between">
        <Group gap="xs">
          <Button
            variant={activeTab === 'table' ? 'secondary' : 'subtle'}
            radius="xl"
            size="sm"
            leftSection={
              <Icon
                name="format_list_bulleted"
                color={activeTab === 'table' ? baseWhite : inkDark}
                variant="filled"
                size={16}
              />
            }
            onClick={() => onActiveTabChange?.('table')}
          >
            Table View
          </Button>
          {showInsightsTab && (
            <Button
              variant={activeTab === 'flora' ? 'secondary' : 'subtle'}
              radius="xl"
              size="sm"
              leftSection={
                <Icon
                  name="auto_awesome"
                  color={activeTab === 'flora' ? baseWhite : purpleBase}
                  variant="filled"
                  size={16}
                />
              }
              onClick={() => onActiveTabChange?.('flora')}
            >
              Insights
            </Button>
          )}
        </Group>
        {activeTab === 'table' && (
          <Group>
            <TextInput
              placeholder={`Search ${isEpic ? i18n.t('common.epics') : i18n.t('common.work_units').toLowerCase()}...`}
              radius="xl"
              size="sm"
              value={searchTerm}
              leftSection={<Icon name="search" variant="filled" size={16} color={inkLight} />}
              style={{ width: 300 }}
              onChange={(event) => {
                const newSearchTerm = event.currentTarget.value;
                setSearchTerm(newSearchTerm);
                const filtered = tasks.filter(
                  (task) =>
                    task.name?.toLowerCase().includes(newSearchTerm.toLowerCase()) ||
                    task.description?.toLowerCase().includes(newSearchTerm.toLowerCase()) ||
                    task.code.toLowerCase().includes(newSearchTerm.toLowerCase()),
                );
                setFilteredTasks(filtered);
                setSelectedPage(1);
              }}
            />
            <Button
              variant="secondary"
              radius="xl"
              size="sm"
              leftSection={<Icon name="download" color={baseWhite} variant="filled" size={16} />}
              onClick={() => handleTaskTableDownloadCSV(isEpic, filteredTasks, i18n.t)}
            >
              Export
            </Button>
          </Group>
        )}
      </Group>

      {activeTab === 'table' &&
        (filteredTasks.length > 0 ? (
          <Table<RiskAnalysisTask>
            data={filteredTasks}
            columns={getTaskTableColumns(isEpic, i18n.t)}
            getRowId={(task: RiskAnalysisTask) => task.id}
            onRowClick={(task: RiskAnalysisTask) => {
              onTaskClick(task);
            }}
            stickyHeader
            pageSize={ITEMS_PER_PAGE}
            currentPage={selectedPage}
            totalItems={filteredTasks.length}
            onPageChange={(page: number) => {
              setSelectedPage(page);
            }}
            customSort={sortData}
          />
        ) : (
          <EmptyFilterResult objectName={isEpic ? i18n.t('common.epics') : i18n.t('common.work_units').toLowerCase()} />
        ))}

      {activeTab === 'flora' && (
        <FloraInsightsView
          insightType={isEpic ? InsightType.EPIC : InsightType.TASK}
          onViewAffectedItems={onInsightClick}
          insightTypeFilters={floraInsightTypeFilters}
          severityFilters={floraSeverityFilters}
          statusFilters={floraStatusFilters}
          viewFilter={floraViewFilter}
        />
      )}
    </Stack>
  );
}
